import { RuleContractLanguageAccordion } from "components/ContractLanguageSection/RuleContractLanguageAccordion";
import {
  IRuleAttachment,
  RuleType,
} from "components/ContractLanguageSection/types";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable, DataTableExpandedRows } from "primereact/datatable";
import { Menu } from "primereact/menu";
import { Tag } from "primereact/tag";
import { ReactNode, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGetProfessionalRulesetForContractQuery } from "store/queries/professionalRuleset";
import styled from "styled-components";

import AddFromDefaultButton from "../../components/AddFromDefaultButton";

const formatCentsToDollars = (cents: number | null | undefined) =>
  cents != null
    ? (cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2 })
    : "—";

// INPATIENT_OUTLIER enum → human label (mirrors ContractView).
const IPO_TRIGGER_LABEL: Record<string, string> = {
  STANDARD_CHARGE_THRESHOLD: "Charge Threshold",
  MODIFIED_CHARGE_THRESHOLD: "Modified Charge Threshold",
  LOS_RANGE: "LOS Range",
  PER_CODE_CHARGE_THRESHOLD: "Per-Code Charge Threshold",
  DISCHARGE_STATUS: "Discharge Status",
  ALWAYS: "Always",
};
const IPO_FORMULA_LABEL: Record<string, string> = {
  PER_DIEM_X_LOS: "Per Diem × LOS",
  PER_DIEM_X_EXCESS_LOS: "Per Diem × Excess LOS",
  FLAT_RATE: "Flat Rate",
  PERCENT_OF_CHARGES: "Percent of Charges",
  PERCENT_OF_EXCESS_CHARGES: "Percent of Excess Charges",
  CASE_RATE_PLUS_PER_DIEM: "Case Rate + Per Diem",
};
const IPO_MODE_LABEL: Record<string, string> = {
  REPLACE: "Replace base",
  ADDITIVE: "Add to base",
  MAX: "Max vs base",
};

const TRANSFER_LOS_FORMULA_LABEL: Record<string, string> = {
  LOS_MINUS_1: "Per Diem × (LOS - 1)",
  LOS: "Per Diem × LOS",
  LOS_PLUS_1: "Per Diem × (LOS + 1)",
};
const TRANSFER_LOS_FORMULA_SHORT: Record<string, string> = {
  LOS_MINUS_1: "(LOS - 1)",
  LOS: "LOS",
  LOS_PLUS_1: "(LOS + 1)",
};

// Per-type config detail for inpatient rulesets — mirrors the per-type blocks
// in ContractView so no information is lost in the unified table. DRG codes are
// omitted here because they have their own column.
const renderInpatientDetails = (ruleset: any): ReactNode => {
  const { config } = ruleset;
  if (!config) return null;

  switch (ruleset.type) {
    case "FIXED_COST":
      return (
        <div>
          <div className="text-600 text-sm mb-1">Fixed Cost</div>
          <div className="text-900">
            CCR: {config.costToChargeRatio?.toFixed(4)}, Threshold: $
            {formatCentsToDollars(config.fixedLossThresholdInCents)}, Outlier:{" "}
            {config.outlierPercent != null
              ? `${config.outlierPercent}%`
              : "80%"}
          </div>
          <div className="text-500 text-xs mt-1">
            Outlier = {config.outlierPercent ?? 80}% × (Charges × CCR − DRG Rate
            − Threshold)
          </div>
        </div>
      );
    case "PASS_THROUGH":
      return (
        <div>
          <div className="text-600 text-sm mb-1">Pass Through</div>
          <div className="text-900">
            Threshold: ${formatCentsToDollars(config.thresholdInCents)},
            Multiplier:{" "}
            {config.chargeMultiplier != null
              ? config.chargeMultiplier.toFixed(4)
              : "—"}
          </div>
          <div className="text-500 text-xs mt-1">
            Expected = Charges ×{" "}
            {config.chargeMultiplier?.toFixed(2) ?? "Multiplier"} (when charges
            exceed threshold)
          </div>
        </div>
      );
    case "EXCESSIVE_LOS":
      return (
        <div>
          <div className="text-600 text-sm mb-1">Excessive LOS</div>
          <div className="text-900">Formula: {config.formula || "—"}</div>
          <div className="text-500 text-xs mt-1">
            Expected = DRG Rate + (Per Diem × Outlier Days). Params come from
            ProviderProcedure DRG fields.
          </div>
        </div>
      );
    case "TRANSFER":
      return (
        <div>
          <div className="text-600 text-sm mb-1">Transfer</div>
          <div className="text-900">
            LOS Formula:{" "}
            {TRANSFER_LOS_FORMULA_LABEL[config.losFormula as string] ||
              "Per Diem × LOS"}
            , Discharge Codes: {config.dischargeStatusCodes?.join(", ") || "—"}
          </div>
          <div className="text-500 text-xs mt-1">
            Expected = min(DRG Rate, Per Diem ×{" "}
            {TRANSFER_LOS_FORMULA_SHORT[config.losFormula as string] || "LOS"})
            when discharge status matches
          </div>
        </div>
      );
    case "STOP_LOSS":
      return (
        <div>
          <div className="text-600 text-sm mb-1">Stop Loss</div>
          <div className="text-900">
            Threshold: {config.thresholdMultiplier}% of DRG, Payment:{" "}
            {config.paymentPercent}% of charges
          </div>
          <div className="text-500 text-xs mt-1">
            When charges &gt; DRG Rate × {config.thresholdMultiplier}%, Expected
            = {config.paymentPercent}% × Total Charges
          </div>
        </div>
      );
    case "CHARGE_OUTLIER":
      return (
        <div>
          <div className="text-600 text-sm mb-1">Charge Outlier</div>
          <div className="text-900">
            {config.thresholdType === "FLAT"
              ? `Threshold: $${formatCentsToDollars(
                  config.thresholdAmountInCents
                )}`
              : `Threshold: ${config.thresholdMultiplier}× base payment`}
            , Outlier: {config.outlierPercent}% of excess
            {config.chargeFloorInCents != null &&
              `, Floor: $${formatCentsToDollars(config.chargeFloorInCents)}`}
          </div>
          <div className="text-500 text-xs mt-1">
            Outlier = {config.outlierPercent}% × (Charges −{" "}
            {config.thresholdType === "FLAT"
              ? "Threshold"
              : `${config.thresholdMultiplier}× Base`}
            )
          </div>
        </div>
      );
    case "INPATIENT_OUTLIER":
      return (
        <div>
          <div className="text-600 text-sm mb-1">Inpatient Outlier Rule</div>
          <div className="text-900">
            Trigger:{" "}
            {IPO_TRIGGER_LABEL[config.trigger?.type] ?? config.trigger?.type},
            Formula:{" "}
            {IPO_FORMULA_LABEL[config.formula?.type] ?? config.formula?.type},
            Mode: {IPO_MODE_LABEL[config.mode] ?? config.mode}
          </div>
          <div className="text-500 text-xs mt-1">
            {config.mode === "REPLACE" &&
              "Replaces base expected when triggered (overrides Transfer too)"}
            {config.mode === "ADDITIVE" &&
              "Added to base expected when triggered"}
            {config.mode === "MAX" &&
              "Competes via Math.max() with base expected"}
          </div>
        </div>
      );
    default:
      return null;
  }
};

// Total / per-code rate detail for exploding fee schedule rulesets — mirrors
// the per-section blocks in ContractView so no information is lost.
const renderExplodingFeeDetails = (ruleset: any): ReactNode => {
  const totalRate = (ruleset.totalRateInCents ?? 0) / 100;
  const codeCount = (ruleset.cptCodes ?? []).length;
  const perCodeRate = codeCount > 0 ? totalRate / codeCount : null;
  return (
    <div>
      <div className="text-900">
        Total Rate: $
        {totalRate.toLocaleString("en-US", { minimumFractionDigits: 2 })}
      </div>
      <div className="text-900">
        Per-Code Rate:{" "}
        {perCodeRate != null
          ? `$${perCodeRate.toLocaleString("en-US", {
              minimumFractionDigits: 2,
            })}`
          : "—"}
      </div>
    </div>
  );
};

const summarizeCodes = (codes: string[]): string => {
  if (!codes.length) return "—";
  const extra = codes.length - 5;
  return codes.slice(0, 5).join(", ") + (extra > 0 ? ` + ${extra} others` : "");
};

// Bundled-code detail for package rulesets — the trigger codes go in the
// CPT/DRG column; bundled codes are shown here so no information is lost.
const renderPackageDetails = (ruleset: any): ReactNode => (
  <div>
    <div className="text-600 text-sm mb-1">Bundled CPT Codes</div>
    <div className="text-900">
      {summarizeCodes((ruleset.bundledCptCodes ?? []) as string[])}
    </div>
  </div>
);

// CLE-2397 — MPDR primary-selection method labels (mirrors PayerRulesTable).
const PRIMARY_SELECTION_METHOD_LABELS: Record<string, string> = {
  ENDO_FAMILY_RVU: "Endo family RVU",
  RVU: "RVU",
  CONTRACTED_DOLLARS: "Contracted dollars",
};

// Professional ruleset config detail — mirrors the Additional Info column on
// the /payer-rules page. Falls back to the same defaults as the standalone
// card when no ruleset record exists for this contract.
const renderProfessionalDetails = (ruleset: any): ReactNode => {
  const primaryMethodLabel =
    PRIMARY_SELECTION_METHOD_LABELS[
      ruleset?.primarySelectionMethod ?? "ENDO_FAMILY_RVU"
    ] ?? "Endo family RVU";
  const compoundBilateralAndMppr = ruleset?.compoundBilateralAndMppr ?? true;
  const endoApplies = ruleset?.endoApplies ?? true;
  const disabledFamilyCount = ruleset?.disabledEndoBaseCptCodes?.length ?? 0;
  const disabledFamilyNote =
    endoApplies && disabledFamilyCount > 0
      ? ` (${disabledFamilyCount} ${
          disabledFamilyCount === 1 ? "family" : "families"
        } turned off)`
      : "";
  return (
    <div className="flex flex-column gap-1">
      <span className="text-900">Primary: {primaryMethodLabel}</span>
      <span className="text-900">
        Compound Bilateral + MPPR: {compoundBilateralAndMppr ? "ON" : "OFF"}
      </span>
      <span className="text-900">
        Endoscopy Rules: {endoApplies ? "ON" : "OFF"}
        {disabledFamilyNote}
      </span>
    </div>
  );
};

// One "Add from default" entry per ruleset type — supplies the list/copy hooks
// to the reused AddFromDefaultButton dialog.
interface IAddFromDefaultOption {
  label: string;
  entityLabel: string;
  useList: any;
  useCopy: any;
}

interface IAdjustmentRulesProps {
  mpdrRulesets?: any[];
  caseRateRulesets?: any[];
  inpatientRulesets?: any[];
  explodingFeeScheduleRulesets?: any[];
  packageRulesets?: any[];
  // Hospital staff can't reach the payer-rules edit pages, so names aren't linked.
  hospitalId?: string | null;
  // "Add from default" support — omitted for hospital staff / unsaved contracts.
  addFromDefaultOptions?: IAddFromDefaultOption[];
  providerId?: string;
  payerId?: number;
  onRuleAdded?: () => void;
  // Whether the Professional ruleset row applies (hidden for facility/anesthesia).
  showProfessional?: boolean;
  // Provider/payer names for the Professional row when no ruleset record exists.
  providerName?: string;
  payerName?: string;
}

// Inpatient category grouping (mirrors ContractView's getCategory).
const INPATIENT_CATEGORY_TITLES: Record<string, string> = {
  OVERRIDE: "Overrides",
  ADJUSTMENT: "Adjustments",
  SAFETY_NET: "Safety Nets",
};

const INPATIENT_CATEGORY_DESCRIPTIONS: Record<string, string> = {
  OVERRIDE: "Replace the base expected amount entirely",
  ADJUSTMENT: "Added to the base expected amount",
  SAFETY_NET: "Compete with the base via Math.max()",
};

const getInpatientCategory = (ruleset: any): string => {
  if (ruleset.type === "TRANSFER" || ruleset.type === "EXCESSIVE_LOS")
    return "OVERRIDE";
  if (ruleset.type === "STOP_LOSS") return "SAFETY_NET";
  if (ruleset.type === "INPATIENT_OUTLIER") {
    const mode = ruleset.config?.mode;
    if (mode === "REPLACE") return "OVERRIDE";
    if (mode === "MAX") return "SAFETY_NET";
    return "ADJUSTMENT";
  }
  return "ADJUSTMENT";
};

interface IDiscount {
  label: string;
  value: number | null | undefined;
}

interface IAdditionalInfo {
  text: string;
  icon?: boolean;
}

interface IAdjustmentRule {
  id: string;
  rowKey: string;
  ruleType: string;
  ruleTypeSubLabel?: string;
  ruleTypeDescription?: string;
  ruleName: string;
  providerName: string;
  payerName: string;
  discounts: IDiscount[];
  cptCodes: string[];
  excludedCptCodes: string[];
  additionalInfo: IAdditionalInfo[];
  details?: ReactNode;
  editPath: string;
  // Contract-language accordion content (shown in the expanded row).
  accordionRuleType: RuleType;
  contractLanguage?: string | null;
  attachments: IRuleAttachment[];
  hasLanguageContent: boolean;
}

// Rule-type tag styling mirrors the /payer-rules table (PayerRulesTable.tsx).
const RULE_TYPE_TAG_PALETTE: Record<
  string,
  { $bg: string; $fg: string; $border: string }
> = {
  MPPR: {
    $bg: "var(--primary-50)",
    $fg: "var(--primary-700)",
    $border: "var(--primary-300)",
  },
  "Case Rate": {
    $bg: "var(--primary-100)",
    $fg: "var(--primary-700)",
    $border: "var(--primary-200)",
  },
  Inpatient: {
    $bg: "var(--primary-100)",
    $fg: "var(--primary-700)",
    $border: "var(--primary-300)",
  },
  "Exploding Fee Schedule": {
    $bg: "var(--primary-50)",
    $fg: "var(--primary-800)",
    $border: "var(--primary-300)",
  },
  Package: {
    $bg: "var(--primary-100)",
    $fg: "var(--primary-800)",
    $border: "var(--primary-200)",
  },
  Professional: {
    $bg: "var(--primary-50)",
    $fg: "var(--primary-700)",
    $border: "var(--primary-200)",
  },
};

const DEFAULT_TAG_PALETTE = {
  $bg: "var(--primary-50)",
  $fg: "var(--primary-800)",
  $border: "var(--primary-300)",
};

const StyledRuleTypeTag = styled(Tag)<{
  $bg: string;
  $fg: string;
  $border: string;
}>`
  &.p-tag.p-component {
    background: ${(p) => p.$bg} !important;
    color: ${(p) => p.$fg} !important;
    border: 1px solid ${(p) => p.$border} !important;
    font-weight: 600;
    font-size: 0.75rem;
    width: fit-content;
    border-radius: 8px;
  }
`;

const StyledDataTable = styled(DataTable)`
  .p-datatable-thead > tr > th {
    background: #ffffff;
  }
`;

/**
 * Unified "Adjustment Rules" section for the contract view. Will replace the
 * separate per-type ruleset cards (MPPR, Case Rate, Inpatient, etc.), listing
 * each rule with its type and name like the Adjustment page.
 */
export default function AdjustmentRules({
  mpdrRulesets,
  caseRateRulesets,
  inpatientRulesets,
  explodingFeeScheduleRulesets,
  packageRulesets,
  hospitalId,
  addFromDefaultOptions,
  providerId,
  payerId,
  onRuleAdded,
  showProfessional,
  providerName,
  payerName,
}: IAdjustmentRulesProps) {
  const navigate = useNavigate();

  // Professional ruleset is fetched per (provider, payer); rendered as one row.
  const { data: professionalRulesetData } =
    useGetProfessionalRulesetForContractQuery(
      { providerId: providerId as string, payerId: payerId as number },
      { skip: !showProfessional || !providerId || payerId == null }
    );
  const professionalRuleset = professionalRulesetData?.data ?? null;
  const [expandedRows, setExpandedRows] = useState<DataTableExpandedRows>({});
  const addMenuRef = useRef<Menu>(null);
  const [activeAddOption, setActiveAddOption] =
    useState<IAddFromDefaultOption | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  // Contract-language / attachments fields shared by every rule type.
  const accordionFields = (ruleset: any, accordionRuleType: RuleType) => {
    const attachments = (ruleset.attachments ?? []) as IRuleAttachment[];
    return {
      accordionRuleType,
      contractLanguage: ruleset.contractLanguage ?? null,
      attachments,
      hasLanguageContent: !!ruleset.contractLanguage || attachments.length > 0,
    };
  };

  const mpprRules: IAdjustmentRule[] = (mpdrRulesets ?? [])
    .filter(Boolean)
    .map((ruleset) => ({
      id: ruleset.id,
      rowKey: `MPPR:${ruleset.id}`,
      ruleType: "MPPR",
      ruleName: ruleset.groupName ?? "Unnamed MPDR Rule Set",
      providerName: ruleset.provider?.name ?? "N/A",
      payerName: ruleset.payer?.name ?? "N/A",
      discounts: [
        { label: "Rank 2", value: ruleset.discountPercentForRank2 },
        { label: "Rank 3", value: ruleset.discountPercentForRank3 },
        { label: "Rank 4", value: ruleset.discountPercentForRank4 },
        { label: "Rank 5+", value: ruleset.discountPercentForRank5Plus },
      ],
      cptCodes: (ruleset.cptCodes ?? []) as string[],
      excludedCptCodes: [],
      additionalInfo: ruleset.allowOnDuplicateCptCodes
        ? [{ text: "Allows duplicate CPT codes", icon: true }]
        : [],
      editPath: `/payer-rules/mppr/edit/${ruleset.id}`,
      ...accordionFields(ruleset, "MPPR"),
    }));

  const caseRateRules: IAdjustmentRule[] = (caseRateRulesets ?? [])
    .filter(Boolean)
    .map((ruleset) => ({
      id: ruleset.id,
      rowKey: `CASE_RATE:${ruleset.id}`,
      ruleType: "Case Rate",
      ruleName: ruleset.groupName ?? "Unnamed Case Rate Rule Set",
      providerName: ruleset.provider?.name ?? "N/A",
      payerName: ruleset.payer?.name ?? "N/A",
      discounts: [],
      cptCodes: (ruleset.cptCodes ?? []) as string[],
      excludedCptCodes: (ruleset.excludedCptCodes ?? []) as string[],
      additionalInfo: [],
      editPath: `/payer-rules/case-rate/edit/${ruleset.id}`,
      ...accordionFields(ruleset, "CASE_RATE"),
    }));

  const inpatientRules: IAdjustmentRule[] = (inpatientRulesets ?? [])
    .filter(Boolean)
    .map((ruleset) => {
      const category = getInpatientCategory(ruleset);
      return {
        id: ruleset.id,
        rowKey: `INPATIENT:${ruleset.id}`,
        ruleType: "Inpatient",
        ruleTypeSubLabel: INPATIENT_CATEGORY_TITLES[category],
        ruleTypeDescription: INPATIENT_CATEGORY_DESCRIPTIONS[category],
        ruleName: ruleset.name ?? "Unnamed Inpatient Ruleset",
        providerName: ruleset.provider?.name ?? "N/A",
        payerName: ruleset.payer?.name ?? "N/A",
        discounts: [],
        cptCodes: (ruleset.drgCodes ?? []) as string[],
        excludedCptCodes: [],
        additionalInfo: [],
        details: renderInpatientDetails(ruleset),
        editPath: `/payer-rules/inpatient-rulesets/edit/${ruleset.id}`,
        ...accordionFields(ruleset, "INPATIENT"),
      };
    });

  const explodingFeeRules: IAdjustmentRule[] = (
    explodingFeeScheduleRulesets ?? []
  )
    .filter(Boolean)
    .map((ruleset) => ({
      id: ruleset.id,
      rowKey: `EXPLODING_FEE:${ruleset.id}`,
      ruleType: "Exploding Fee Schedule",
      ruleName: ruleset.groupName ?? "Unnamed Exploding Fee Schedule Ruleset",
      providerName: ruleset.provider?.name ?? "N/A",
      payerName: ruleset.payer?.name ?? "N/A",
      discounts: [],
      cptCodes: (ruleset.cptCodes ?? []) as string[],
      excludedCptCodes: [],
      additionalInfo: [],
      details: renderExplodingFeeDetails(ruleset),
      editPath: `/payer-rules/exploding-fee-schedules/edit/${ruleset.id}`,
      ...accordionFields(ruleset, "EXPLODING_FEE"),
    }));

  const packageRules: IAdjustmentRule[] = (packageRulesets ?? [])
    .filter(Boolean)
    .map((ruleset) => ({
      id: ruleset.id,
      rowKey: `PACKAGE:${ruleset.id}`,
      ruleType: "Package",
      ruleName: ruleset.groupName ?? "Unnamed Package Ruleset",
      providerName: ruleset.provider?.name ?? "N/A",
      payerName: ruleset.payer?.name ?? "N/A",
      discounts: [],
      cptCodes: (ruleset.triggerCptCodes ?? []) as string[],
      excludedCptCodes: [],
      additionalInfo: [],
      details: renderPackageDetails(ruleset),
      editPath: `/payer-rules/packages/edit/${ruleset.id}`,
      ...accordionFields(ruleset, "PACKAGE"),
    }));

  // Always show a Professional row when applicable — like the standalone card,
  // it falls back to engine defaults when no ruleset record exists.
  const professionalRules: IAdjustmentRule[] = showProfessional
    ? [
        {
          id: professionalRuleset?.id ?? "professional-default",
          rowKey: `PROFESSIONAL:${professionalRuleset?.id ?? "default"}`,
          ruleType: "Professional",
          ruleName: "—",
          providerName:
            professionalRuleset?.provider?.name ?? providerName ?? "N/A",
          payerName: professionalRuleset?.payer?.name ?? payerName ?? "N/A",
          discounts: [],
          cptCodes: [],
          excludedCptCodes: [],
          additionalInfo: [],
          details: renderProfessionalDetails(professionalRuleset),
          editPath: professionalRuleset
            ? `/payer-rules/professional-rulesets/edit/${professionalRuleset.id}`
            : "",
          // Professional rulesets have no contract-language accordion.
          accordionRuleType: "MPPR",
          contractLanguage: null,
          attachments: [],
          hasLanguageContent: false,
        },
      ]
    : [];

  const rules = [
    ...mpprRules,
    ...caseRateRules,
    ...inpatientRules,
    ...explodingFeeRules,
    ...packageRules,
    ...professionalRules,
  ];

  const ruleNameTemplate = (row: IAdjustmentRule) => {
    // Only admins can open the payer-rules edit pages; hospital staff see plain
    // text. The Professional row has no name, so it's never linked.
    if (hospitalId || row.ruleType === "Professional") {
      return <span className="text-900">{row.ruleName}</span>;
    }
    return (
      <a
        href={row.editPath}
        onClick={(e) => {
          e.preventDefault();
          navigate(row.editPath);
        }}
        className="text-primary no-underline hover:underline cursor-pointer"
      >
        {row.ruleName}
      </a>
    );
  };

  const ruleTypeTemplate = (row: IAdjustmentRule) => {
    const palette = RULE_TYPE_TAG_PALETTE[row.ruleType] ?? DEFAULT_TAG_PALETTE;
    return (
      <div className="flex flex-column gap-1">
        <StyledRuleTypeTag
          value={row.ruleType}
          $bg={palette.$bg}
          $fg={palette.$fg}
          $border={palette.$border}
        />
        {row.ruleTypeSubLabel && (
          <span className="text-sm text-gray-500">{row.ruleTypeSubLabel}</span>
        )}
        {row.ruleTypeDescription && (
          <span className="text-xs text-gray-500">
            {row.ruleTypeDescription}
          </span>
        )}
      </div>
    );
  };

  const discountsTemplate = (row: IAdjustmentRule) => {
    if (!row.discounts.length) {
      return <span className="text-600">—</span>;
    }
    return (
      <div className="flex flex-column gap-1">
        {row.discounts.map((d) => (
          <span key={d.label} className="text-900">
            {d.label}: {d.value ?? 0}%
          </span>
        ))}
      </div>
    );
  };

  const codesTemplate = (codes: string[], emptyLabel: string) => {
    if (!codes.length) {
      return <span className="text-600">{emptyLabel}</span>;
    }
    const extra = codes.length - 5;
    return (
      <span className="text-900">
        {codes.slice(0, 5).join(", ")}
        {extra > 0 && <span className="text-600"> + {extra} others</span>}
      </span>
    );
  };

  const additionalInfoTemplate = (row: IAdjustmentRule) => {
    if (row.details) {
      return row.details;
    }
    if (!row.additionalInfo.length) {
      return <span className="text-600">—</span>;
    }
    return (
      <div className="flex flex-column gap-1">
        {row.additionalInfo.map((info) => (
          <span key={info.text} className="text-900">
            {info.icon && (
              <i className="pi pi-check-circle text-green-500 mr-2" />
            )}
            {info.text}
          </span>
        ))}
      </div>
    );
  };

  // Expanded row reveals the contract-language / attachments accordion that the
  // old per-section view rendered under each ruleset.
  const rowExpansionTemplate = (row: IAdjustmentRule) => (
    <div className="px-3 pb-2">
      <RuleContractLanguageAccordion
        ruleType={row.accordionRuleType}
        ruleId={row.id}
        contractLanguage={row.contractLanguage}
        attachments={row.attachments}
      />
    </div>
  );

  const canAddFromDefault =
    !!addFromDefaultOptions?.length && !!providerId && payerId != null;

  const addMenuItems = (addFromDefaultOptions ?? []).map((option) => ({
    label: option.label,
    icon: "pi pi-copy",
    command: () => {
      setActiveAddOption(option);
      setAddDialogOpen(true);
    },
  }));

  const title = (
    <div className="flex justify-content-between align-items-center">
      <span>Adjustment Rules</span>
      {canAddFromDefault && (
        <>
          <Button
            label="Add from default"
            icon="pi pi-plus"
            className="p-button-sm p-button-text"
            onClick={(e) => addMenuRef.current?.toggle(e)}
          />
          <Menu model={addMenuItems} popup ref={addMenuRef} />
        </>
      )}
    </div>
  );

  return (
    <Card title={title} className="mb-4">
      {/* Reused copy-from-default dialog, driven by the rule-type menu above. */}
      {canAddFromDefault && activeAddOption && (
        <AddFromDefaultButton
          key={activeAddOption.label}
          providerId={providerId as string}
          payerId={payerId as number}
          entityLabel={activeAddOption.entityLabel}
          useList={activeAddOption.useList}
          useCopy={activeAddOption.useCopy}
          onSuccess={onRuleAdded}
          open={addDialogOpen}
          onOpenChange={setAddDialogOpen}
          hideTrigger
        />
      )}
      {rules.length > 0 ? (
        <StyledDataTable
          value={rules}
          dataKey="rowKey"
          responsiveLayout="scroll"
          className="p-datatable-sm"
          scrollable
          expandedRows={expandedRows}
          onRowToggle={(e) => setExpandedRows(e.data as DataTableExpandedRows)}
          rowExpansionTemplate={rowExpansionTemplate}
        >
          <Column
            expander={(row: IAdjustmentRule) => row.hasLanguageContent}
            headerStyle={{ width: "2.5rem", flex: "0 0 2.5rem" }}
            bodyStyle={{ width: "2.5rem", flex: "0 0 2.5rem" }}
          />
          <Column
            field="ruleType"
            header="Rule Type"
            body={ruleTypeTemplate}
            style={{ minWidth: "10rem" }}
          />
          <Column
            field="ruleName"
            header="Rule Name"
            body={ruleNameTemplate}
            style={{ minWidth: "12rem" }}
          />
          <Column
            field="providerName"
            header="Provider Name"
            style={{ minWidth: "10rem" }}
          />
          <Column
            header="Discounts"
            body={discountsTemplate}
            style={{ minWidth: "12rem" }}
          />
          <Column
            header="CPT/DRG Codes"
            body={(row: IAdjustmentRule) =>
              codesTemplate(row.cptCodes, "No codes configured")
            }
            style={{ minWidth: "8rem" }}
          />
          <Column
            header="Excluded CPT Codes"
            body={(row: IAdjustmentRule) =>
              codesTemplate(row.excludedCptCodes, "—")
            }
            style={{ minWidth: "8rem" }}
          />
          <Column
            header="Additional Info"
            body={additionalInfoTemplate}
            style={{ minWidth: "14rem" }}
          />
        </StyledDataTable>
      ) : (
        <div className="text-center p-4 text-600">
          No adjustment rules to display yet.
        </div>
      )}
    </Card>
  );
}
